/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

// Shared foundational style overrides (brand-agnostic)
import styles from '../theme/foundations/styles'
import colors from './foundations/colors'
import gradients from '@salesforce/retail-react-app/app/theme/foundations/gradients'
import sizes from './foundations/sizes'
import space from '@salesforce/retail-react-app/app/theme/foundations/space'
import layerStyles from './foundations/layerStyles'
import shadows from '@salesforce/retail-react-app/app/theme/foundations/shadows'
import fonts from './foundations/fonts'
import {typographyStyle, typographyUnits} from './foundations/typography'

// Base component style overrides
import Alert from './components/base/alert'
import Accordion from './components/base/accordion'
import Badge from '@salesforce/retail-react-app/app/theme/components/base/badge'
import Button from './components/base/button'
import Checkbox from '../theme/components/base/checkbox'
import Container from '@salesforce/retail-react-app/app/theme/components/base/container'
import Drawer from './components/base/drawer'
import FormLabel from './components/base/formLabel'
import Form from './components/base/form'
import Icon from '@salesforce/retail-react-app/app/theme/components/base/icon'
import Input from './components/base/input'
import Modal from '@salesforce/retail-react-app/app/theme/components/base/modal'
import Radio from './components/base/radio'
import Select from './components/base/select'
import Textarea from './components/base/textarea'
import Skeleton from '@salesforce/retail-react-app/app/theme/components/base/skeleton'
import Tooltip from './components/base/tooltip'
import Popover from './components/base/popover'
import Text from './components/base/text'
import Swiper from './components/base/swiper'
import Heading from './components/base/heading'
import CustomToast from './components/base/customToast'

// Project Component style overrides
import App from '@salesforce/retail-react-app/app/theme/components/project/_app'
import Breadcrumb from './components/project/breadcrumb'
import Header from './components/project/header'
import ListMenu from './components/project/list-menu'
import Footer from './components/project/footer'
import CheckoutFooter from './components/project/checkout-footer'
import LinksList from './components/project/links-list'
import DrawerMenu from './components/project/drawer-menu'
import NestedAccordion from '@salesforce/retail-react-app/app/theme/components/project/nested-accordion'
import LocaleSelector from '@salesforce/retail-react-app/app/theme/components/project/locale-selector'
import OfflineBanner from '@salesforce/retail-react-app/app/theme/components/project/offline-banner'
import Pagination from '@salesforce/retail-react-app/app/theme/components/project/pagination'
import ProductTile from './components/project/product-tile'
import ProductLikes from './components/project/product-likes'
import SocialIcons from '@salesforce/retail-react-app/app/theme/components/project/social-icons'
import SwatchGroup from './components/project/swatch-group'
import ImageGallery from './components/project/image-gallery'
import Cart from './components/project/cart'
import Loyalty from './components/base/loyalty'
import CustomAdyen from './components/base/adyen'
import CustomAccountAdyenPayments from './components/base/accountAdyenPayments'
import DeliveryOptions from './components/project/delivery-options'
import DisplayPrice from './components/project/display-price'

import StorePickup from './components/project/store-pickup'
import WriteReview from './components/project/write-review'
import CustomerSay from './components/project/customer-say'
import Newsletter from './components/project/newsletter'
import FooterNavigationLinks from './components/project/footer-navigation-links'
import AddressBook from './components/project/address-book'
import MyRewards from './components/project/my-rewards'
import StoreLocator from './components/project/store-locator'
import StoreInfo from './components/project/store-info'
import FindInStoreContent from './components/project/find-in-store-content'
import FindInStoreList from './components/project/find-in-store-list'
import FindInStoreModal from './components/project/find-in-store-modal'
import LongDescriptionModal from './components/project/long-description-modal'
import ShippingPickupModal from './components/project/shipping-pickup-modal'
import InformationAccordion from './components/project/information-accordion'
import UnselectedVariationGroups from './components/project/unselected-variation-groups'
import FewItemsLeft from './components/project/few-items-left'
import ErrorMessage from './components/project/error-message'
import ProductCardWithImage from './components/project/product-card-with-image'
import GridViewSelector from './components/project/grid-view-selector'
import ItemsCounter from './components/project/items-counter'
import ProductGrid from './components/project/product-grid'
import ProductItem from './components/project/product-item'
import QuickView from './components/project/quick-view'
import SearchModal from './components/project/search-modal'
import SearchOverlay from './components/project/search-overlay'
import SizeChartDrawer from './components/project/size-chart-drawer'
import TopBar from './components/project/top-bar'
import InfoCard from './components/project/info-card'
import QuantityPicker from './components/project/quantity-picker'
import CountrySelector from './components/project/country-selector'
import CountrySelectorConfirmation from './components/project/country-selector-confirmation'
import PageFilters from './components/project/page-filters'
import Refinements from './components/project/refinements'
import ProductView from './components/project/product-view'
import Sort from './components/project/sort'
import PlpSrpPages from './components/project/plp-srp-pages'
import ApproachingDiscounts from './components/project/approaching-discounts'
import PromoCode from './components/project/promo-code'
import LoyaltyCoupon from './components/project/loyalty-coupon'
import MiniCart from './components/project/mini-cart'
import MiniCartOrderSummary from './components/project/mini-cart-order-summary'
import MiniCartCMSAccordion from './components/project/mini-cart-cms-accordion'
import CustomCallout from './components/project/custom-callout'
import GlobalErrorCallout from './components/project/global-error-callout'
import WebFloatingSelect from './components/project/web-floating-select'
import OrderSummary from './components/project/order-summary'
import CheckoutProgressBar from './components/project/checkout-progress-bar'
import CheckoutHeader from './components/project/checkout-header'
import NeedHelpDrawer from './components/project/need-help-drawer'
import CheckoutPage from './components/project/checkout-page'
import CheckoutConfirmation from './components/project/checkout-confirmation'
import AddressValidationDrawer from './components/project/address-validation-drawer'
import ShippingAddress from './components/project/shipping-address'
import ShippingOptions from './components/project/shipping-options'
import ShippingAddressSelection from './components/project/shipping-address-selection'
import Payment from './components/project/payment'
import GiftCard from './components/project/gift-card'
import AdyenDropin from './components/project/dropin'
import AddressCardTheme from './components/project/address-card'
import AddressDisplayTheme from './components/project/address-display'
import OptIn from './components/project/opt-in'
import PrivacyPolicyDrawer from './components/project/privacy-policy-drawer'
import NotifyMe from './components/project/notify-me'
import ShopSimilarModal from './components/project/shop-similar-modal'
import AddToCartConfirmationDrawer from './components/project/add-to-cart-confirmation-drawer'
import ProductBadges from './components/project/badges'

/* START: CMS Styling */

/* Global */
import GlobalCMS from '../components/optimizely/styles/index'

/* Legacy CMS layouts/ and assets/ folders removed — all imports stripped as part of GDI cleanup. */

/* Common without typename or used directly on other components */
import CtaBlock from '../components/optimizely/common/ctaBlock/styles/index'
import GridInteruptorBlock from '../components/optimizely/common/gridInteruptorBlock/styles/index'
import GDIContentPage from '../components/optimizely/pages/GDIContentPage/styles/index'
import GDICollapsibleItemBlock from '../components/optimizely/gdi/assets/gdiCollapsibleItemBlock/styles/index'
import GDITableLayoutBlock from '../components/optimizely/gdi/layouts/gdiTableLayoutBlock/styles/index'
import GDITabsLayoutBlock from '../components/optimizely/gdi/layouts/gdiTabsLayoutBlock/styles/index'

/* END: CMS Styling */

/**
 * Shared theme overrides used by all brands.
 * Brand-specific overrides (colors, fonts) are layered on top of this
 * via extendTheme in theme/index.js.
 */
export const overrides = {
    styles,
    layerStyles,
    colors,
    sizes,
    space,
    gradients,
    shadows,
    fonts,
    fontSizes: typographyUnits,
    textStyles: typographyStyle,
    brandId: 'garage',
    components: {
        // base components
        Accordion,
        Alert,
        Badge,
        Button,
        Checkbox,
        Container,
        Drawer,
        FormLabel,
        Form,
        Icon,
        Input,
        Modal,
        Popover,
        Radio,
        Select,
        Textarea,
        Skeleton,
        Tooltip,
        Text,
        Swiper,
        Heading,
        CustomToast,
        InfoCard,

        // project components
        App,
        Breadcrumb,
        Header,
        Footer,
        CheckoutFooter,
        LinksList,
        ListMenu,
        DrawerMenu,
        NestedAccordion,
        LocaleSelector,
        OfflineBanner,
        SocialIcons,
        Pagination,
        ProductTile,
        ProductCardWithImage,
        ProductLikes,
        SwatchGroup,
        ImageGallery,
        Loyalty,
        Cart,
        StorePickup,
        WriteReview,
        CustomerSay,
        CustomAdyen,
        Newsletter,
        FooterNavigationLinks,
        AddressBook,
        CustomAccountAdyenPayments,
        MyRewards,
        StoreLocator,
        StoreInfo,
        FindInStoreContent,
        FindInStoreList,
        FindInStoreModal,
        LongDescriptionModal,
        ShippingPickupModal,
        InformationAccordion,
        UnselectedVariationGroups,
        DeliveryOptions,
        DisplayPrice,
        FewItemsLeft,
        ErrorMessage,
        GridViewSelector,
        ItemsCounter,
        ProductGrid,
        ProductItem,
        QuickView,
        SearchModal,
        SearchOverlay,
        SizeChartDrawer,
        TopBar,
        QuantityPicker,
        CountrySelector,
        CountrySelectorConfirmation,
        PageFilters,
        Refinements,
        ProductView,
        Sort,
        PlpSrpPages,
        ApproachingDiscounts,
        PromoCode,
        LoyaltyCoupon,
        OrderSummary,
        MiniCart,
        MiniCartOrderSummary,
        MiniCartCMSAccordion,
        CustomCallout,
        GlobalErrorCallout,
        WebFloatingSelect,
        CheckoutProgressBar,
        CheckoutHeader,
        NeedHelpDrawer,
        CheckoutPage,
        CheckoutConfirmation,
        AddressValidationDrawer,
        ShippingAddress,
        ShippingOptions,
        ShippingAddressSelection,
        Payment,
        GiftCard,
        AdyenDropin,
        AddressCard: AddressCardTheme,
        AddressDisplay: AddressDisplayTheme,
        OptIn,
        PrivacyPolicyDrawer,
        NotifyMe,
        ShopSimilarModal,
        AddToCartConfirmationDrawer,
        ProductBadges,

        /* START - CMS Styling*/

        GlobalCMS,
        // Legacy CMS Layouts/Assets (HeroPanelBlock, WidgetGridBlock, CtaItemBlock, etc.) removed in GDI cleanup.

        /* Common */
        CtaBlock,
        GridInteruptorBlock,
        GDIContentPage,
        GDICollapsibleItemBlock,
        GDITableLayoutBlock,
        GDITabsLayoutBlock

        /* END - CMS Styling*/
    }
}
